<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Catégories ({{ totalRows }})</h1>
          <small class="text-muted">
            Gestion des catégories de produits.
          </small>
        </div>

        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['categories', 'Category']" label="Catégories" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>

          <button
            class="btn btn-primary btn-sm btn-icon-label"
            type="button"
            @click="openCreateDialog"
          >
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouvelle catégorie</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <!-- Filtres -->
      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
      <div class="row g-2 align-items-end">
        <div class="col-md-3 d-flex align-items-center">
          <div class="form-check d-flex align-items-center gap-2 ps-0">
            <PrimevueCheckbox
              v-model="filters.only_active"
              binary
              inputId="onlyActiveCategories"
              @change="fetchCategories"
            />
            <label class="form-check-label small" for="onlyActiveCategories">
              Afficher uniquement les catégories actives
            </label>
          </div>
        </div>
        <div class="col-12 module-filter-actions mt-2">
          <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
            <i class="fa fa-rotate-left"></i>
            <span class="btn-label ms-1">Réinitialiser</span>
          </button>
          <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchCategories">
            <i class="fa fa-search"></i>
            <span class="btn-label ms-1">Filtrer</span>
          </button>
        </div>
      </div>
        </div>
      </div>

      <BootstrapDataTable
        :rows="filteredCategories"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkUpdateCategories"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectCategory"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :active-filter-count="activeFilterCount"
        state-key="categories.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher nom ou code..."
        detail-route-name="CategoryShow"
        empty-text="Aucune catégorie trouvée."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @refresh="fetchCategories"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button type="button" class="btn btn-sm btn-outline-success" :disabled="bulkActionPending || isOverLimit" @click="bulkSetCategoryStatus('activate')">
            <i class="fa fa-check me-1"></i>Activer {{ selectedCount }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" :disabled="bulkActionPending || isOverLimit" @click="bulkSetCategoryStatus('deactivate')">
            <i class="fa fa-ban me-1"></i>Désactiver {{ selectedCount }}
          </button>
        </template>

        <template #cell-image_url="{ row }">
          <div class="category-list-image">
            <img v-if="row.image_url" :src="row.image_url" :alt="`Image ${row.name}`" />
            <i v-else class="fa fa-image text-muted"></i>
          </div>
        </template>

        <template #cell-position="{ row }">
          {{ row.position ?? 0 }}
        </template>

        <template #cell-products_count="{ row }">
          <span class="badge bg-light text-dark">
            {{ row.products_count || 0 }}
          </span>
        </template>

        <template #cell-is_active="{ row }">
          <span
            class="badge"
            :class="row.is_active ? 'bg-success' : 'bg-secondary'"
          >
            {{ row.is_active ? 'Oui' : 'Non' }}
          </span>
        </template>
      </BootstrapDataTable>

      <!-- Dialog création / édition -->
      <PrimevueDialog
        v-model:visible="formDialogVisible"
        :modal="true"
        :style="{ width: '600px' }"
        :closable="false"
      >
        <template #header>
          <h5 class="mb-0">
            {{ formDialogMode === 'create' ? 'Nouvelle catégorie' : 'Modifier la catégorie' }}
          </h5>
        </template>

        <CategoryForm
          :mode="formDialogMode"
          :category="selectedCategory"
          :submitting="isSavingForm"
          @save="handleSave"
          @cancel="closeFormDialog"
        />
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import CategoryForm from './CategoryForm.vue';
import {
  countActiveFilters,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';
import { bulkActionViewMixin } from '@/support/bulkActions';
import { hasPermission } from '@/support/permissions';

const FILTER_MODULE = 'categories';
const DEFAULT_FILTERS = {
  search: '',
  only_active: true,
};

export default {
  name: 'CategoriesView',
  mixins: [bulkActionViewMixin],
  components: {
    CategoryForm,
  },
  data() {
    return {
      categories: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,
      table: {
        sortBy: 'position',
        sortDir: 'asc',
      },
      tableColumns: [
        { key: 'image_url', label: 'Image', class: 'text-center', style: 'width: 76px;' },
        { key: 'position', label: '#', sortable: true, class: 'text-center', style: 'width: 70px;' },
        { key: 'name', label: 'Nom', sortable: true },
        { key: 'code', label: 'Code', sortable: true },
        { key: 'products_count', label: 'Produits', sortable: true, class: 'text-center' },
        { key: 'is_active', label: 'Actif', sortable: true, class: 'text-center' },
      ],

      formDialogVisible: false,
      formDialogMode: 'create', // 'create' | 'edit'
      selectedCategory: null,
      isSavingForm: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store?.getters?.['auth/currentUser'] || null;
    },
    canBulkUpdateCategories() {
      return hasPermission(this.currentUser, 'products.update');
    },
    totalRows() {
      return this.filteredCategories.length;
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
    filteredCategories() {
      const term = this.filters.search.trim().toLowerCase();
      if (!term) return this.categories;

      return this.categories.filter((c) => {
        return (
          (c.name && c.name.toLowerCase().includes(term)) ||
          (c.code && c.code.toLowerCase().includes(term))
        );
      });
    },
  },
  mounted() {
    this.restoreFilters();
    this.fetchCategories();
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    canSelectCategory(row) {
      return row?.type !== 'system' && !row?.system_key;
    },

    async bulkSetCategoryStatus(action) {
      const isActivation = action === 'activate';

      await this.performBulkAction({
        url: `/categories/bulk/${action}`,
        method: 'patch',
        itemLabel: 'catégorie',
        confirmation: (count) => ({
          header: isActivation ? 'Activer les catégories' : 'Désactiver les catégories',
          message: `${isActivation ? 'Activer' : 'Désactiver'} les ${count} catégories sélectionnées ?`,
          acceptLabel: `${isActivation ? 'Activer' : 'Désactiver'} ${count}`,
          acceptClass: isActivation ? 'p-button-success' : 'p-button-danger',
        }),
        refresh: () => this.fetchCategories(),
      });
    },

    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      this.filters = state.values;
      this.filtersVisible = state.visible;
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.fetchCategories();
    },

    handleTableSearch(search) {
      this.filters.search = search;
    },

    async fetchCategories(options = {}) {
      this.isLoading = true;
      this.errorMessage = '';
      if (!options.preserveSuccess) {
        this.successMessage = '';
      }

      try {
        const res = await this.$api.get('/categories', {
          params: { only_active: this.filters.only_active ? 'true' : 'false' },
        });
        this.categories = res.data.data || res.data || [];
      } catch (e) {
        console.error('Erreur chargement catégories', e);
        this.errorMessage = 'Impossible de charger les catégories.';
      } finally {
        this.isLoading = false;
      }
    },

    openCreateDialog() {
      this.formDialogMode = 'create';
      this.selectedCategory = null;
      this.formDialogVisible = true;
    },

    openEditDialog(row) {
      this.formDialogMode = 'edit';
      this.selectedCategory = row;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
      this.selectedCategory = null;
    },

    async handleSave(payload) {
      if (this.isSavingForm) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isSavingForm = true;

      try {
        const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
        const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined;

        if (this.formDialogMode === 'create') {
          const response = isFormData
            ? await this.$api.post('/categories', payload, config)
            : await this.$api.post('/categories', payload);
          this.successMessage = this.creationSuccessMessage(
            'Catégorie créée avec succès.',
            response.data?.data?.replication_summary
          );
        } else {
          const id = isFormData
            ? (payload.get('id') || (this.selectedCategory && this.selectedCategory.id))
            : (payload.id || (this.selectedCategory && this.selectedCategory.id));

          if (isFormData) {
            await this.$api.post(`/categories/${id}`, payload, config);
          } else {
            await this.$api.put(`/categories/${id}`, payload);
          }
          this.successMessage = 'Catégorie mise à jour avec succès.';
        }

        this.formDialogVisible = false;
        await this.fetchCategories({ preserveSuccess: true });
      } catch (error) {
        console.error('Erreur enregistrement catégorie', error);
        const errors = error.response && error.response.data && error.response.data.errors;
        const msg = errors
          ? Object.values(errors).flat().join(' ')
          : 'Une erreur est survenue lors de la sauvegarde de la catégorie.';
        this.errorMessage = msg;
      } finally {
        this.isSavingForm = false;
      }
    },

    async deleteCategory(row) {
      if ((row.products_count || 0) > 0) {
        alert(
          "Impossible de supprimer cette catégorie car des produits y sont associés. " +
            "Vous pouvez déplacer les produits vers une autre catégorie ou désactiver cette catégorie."
        );
        return;
      }

      const ok = await this.$confirmAction(`Supprimer la catégorie "${row.name}" ?`);
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.delete(`/categories/${row.id}`);
        this.successMessage = 'Catégorie supprimée.';
        await this.fetchCategories({ preserveSuccess: true });
      } catch (error) {
        console.error('Erreur suppression catégorie', error);
        const errors = error.response && error.response.data && error.response.data.errors;
        const msg = errors
          ? Object.values(errors).flat().join(' ')
          : "Une erreur est survenue lors de la suppression de la catégorie.";
        this.errorMessage = msg;
      }
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
    },

    creationSuccessMessage(baseMessage, summary) {
      if (!summary) {
        return baseMessage;
      }

      const created = Number(summary.created_count || 0);
      const skipped = Number(summary.skipped_count || 0);
      const parts = [`${baseMessage} Créée dans ${created} autre(s) site(s).`];

      if (skipped > 0) {
        parts.push(`${skipped} site(s) ignoré(s) car une catégorie du même nom existe déjà.`);
      }

      return parts.join(' ');
    },
  },
};
</script>

<style scoped>
.category-list-image {
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 0.5rem;
  display: inline-flex;
  height: 2.5rem;
  justify-content: center;
  overflow: hidden;
  width: 2.5rem;
}

.category-list-image img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}
</style>
